import { AfterViewInit, Directive, ElementRef, OnDestroy } from "@angular/core";

@Directive({
  selector: "p-datepicker[appDatepickerMask], p-datePicker[appDatepickerMask]",
  standalone: true,
})
export class DatepickerMaskDirective implements AfterViewInit, OnDestroy {
  private inputEl: HTMLInputElement | null = null;
  private readonly MASK = "dd/MM/aaaa HH:mm";
  private readonly SEPARATORS = new Set(["/", " ", ":"]);

  private boundOnInput = this.onInput.bind(this);
  private boundOnKeydown = this.onKeydown.bind(this);
  private boundOnFocus = this.onFocus.bind(this);

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    this.inputEl = this.el.nativeElement.querySelector("input");
    if (!this.inputEl) return;

    this.inputEl.placeholder = this.MASK;
    this.inputEl.addEventListener("input", this.boundOnInput);
    this.inputEl.addEventListener("keydown", this.boundOnKeydown);
    this.inputEl.addEventListener("focus", this.boundOnFocus);
  }

  ngOnDestroy() {
    if (!this.inputEl) return;
    this.inputEl.removeEventListener("input", this.boundOnInput);
    this.inputEl.removeEventListener("keydown", this.boundOnKeydown);
    this.inputEl.removeEventListener("focus", this.boundOnFocus);
  }

  private onFocus() {
    if (!this.inputEl || this.inputEl.value) return;
    this.inputEl.placeholder = this.MASK;
  }

  private onKeydown(e: KeyboardEvent) {
    if (e.key === "Backspace" && this.inputEl) {
      const pos = this.inputEl.selectionStart ?? 0;
      const val = this.inputEl.value;

      if (pos > 0 && this.SEPARATORS.has(val[pos - 1])) {
        e.preventDefault();
        const newVal = val.slice(0, pos - 2) + val.slice(pos);
        this.inputEl.value = this.applyMask(newVal.replace(/\D/g, ""));
        const newPos = Math.max(0, pos - 2);
        this.inputEl.setSelectionRange(newPos, newPos);
      }
    }
  }

  private onInput() {
    if (!this.inputEl) return;

    const cursorPos = this.inputEl.selectionStart ?? 0;
    const rawBefore = this.inputEl.value;
    const digits = rawBefore.replace(/\D/g, "").slice(0, 12);
    const masked = this.applyMask(digits);

    if (masked !== rawBefore) {
      this.inputEl.value = masked;

      const newPos = this.adjustCursor(
        masked,
        cursorPos,
        rawBefore.length,
        masked.length,
      );
      this.inputEl.setSelectionRange(newPos, newPos);
    }
  }

  private applyMask(digits: string): string {
    let result = "";
    for (let i = 0; i < digits.length && i < 12; i++) {
      if (i === 2 || i === 4) result += "/";
      if (i === 8) result += " ";
      if (i === 10) result += ":";
      result += digits[i];
    }
    return result;
  }

  private adjustCursor(
    masked: string,
    oldPos: number,
    oldLen: number,
    newLen: number,
  ): number {
    const diff = newLen - oldLen;
    let pos = oldPos + diff;

    if (
      pos > 0 &&
      pos < masked.length &&
      this.SEPARATORS.has(masked[pos - 1]) &&
      diff > 0
    ) {
      pos++;
    }

    return Math.min(pos, masked.length);
  }
}
